export interface UrlStateCallbacks {
  showLocation: (id: string, isDeeplink: boolean) => void;
}

export interface UrlStateOptions {
  param?: string;
}

/**
 * Synchronizes the current location with the browser URL.
 * Enables bookmarking and sharing of specific map locations.
 */
export class UrlState {
  private param: string;
  private callbacks: UrlStateCallbacks;

  constructor(callbacks: UrlStateCallbacks, options: UrlStateOptions = {}) {
    this.param = options.param || 'location';
    this.callbacks = callbacks;

    this.init();
  }

  private init(): void {
    window.addEventListener('popstate', (e: PopStateEvent) => {
      if (e.state) {
        this.check();
      }
    });
  }

  /**
   * Check URL for location parameter and show location if found
   */
  public check(): void {
    const id = this.getUrlParam(this.param);
    if (id) {
      this.callbacks.showLocation(id, true);
    }
  }

  /**
   * Get URL parameter value by name (public for mapReady check)
   */
  public getUrlParam(name: string): string {
    const escapedName = name.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp('[\\?&]' + escapedName + '=([^&#]*)');
    const results = regex.exec(window.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  }

  /**
   * Update URL with location parameter
   */
  public update(id: string): void {
    let url: string;

    if (typeof window.URL !== 'undefined') {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set(this.param, id);
      url = urlObj.href;
    } else {
      // Fallback for older browsers
      url = window.location.protocol + '//' + window.location.host + window.location.pathname + '?' + this.param + '=' + id;
    }

    window.history.pushState({ path: url }, '', url);
  }

  /**
   * Clear location parameter from URL
   */
  public clear(): void {
    let url: string;

    if (typeof window.URL !== 'undefined') {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.delete(this.param);
      url = urlObj.href;
    } else {
      // Fallback for older browsers
      url = window.location.pathname;
    }

    window.history.pushState('', document.title, url);
  }
}
